package br.com.heyagenda.data.remote

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable

@Serializable
data class AppointmentDto(
    val id: String = "",
    val clientName: String = "",
    val clientPhone: String? = null,
    val serviceName: String = "",
    val date: String = "",
    val startTime: String = "",
    val endTime: String = "",
    val priceFormatted: String = "",
    val status: String = "CONFIRMED",
    val paymentMethod: String = "",
    val notes: String? = null,
    val startUtcIso: String = "",
    val endUtcIso: String = ""
)

interface FirestoreAppointmentDataSource {
    fun observeAppointments(uid: String): Flow<List<AppointmentDto>>
    suspend fun getAppointments(uid: String): List<AppointmentDto>
    fun observeAppointmentsForDate(uid: String, dateIso: String): Flow<List<AppointmentDto>>
    suspend fun getAppointmentsForDate(uid: String, dateIso: String): List<AppointmentDto>
    fun observeAppointmentsInRange(uid: String, startDateIso: String, endDateIso: String): Flow<List<AppointmentDto>>
    suspend fun getAppointmentsInRange(uid: String, startDateIso: String, endDateIso: String): List<AppointmentDto>
    suspend fun getAppointmentsPaged(uid: String, limit: Int = 20, startAfterDate: String? = null): List<AppointmentDto>
    fun observeAppointmentById(uid: String, appointmentId: String): Flow<AppointmentDto?>
    suspend fun saveAppointment(uid: String, appointment: AppointmentDto): Result<Unit>
    suspend fun updateAppointmentStatus(uid: String, appointmentId: String, status: String): Result<Unit>
    suspend fun updateAppointmentPaymentMethod(uid: String, appointmentId: String, paymentMethod: String): Result<Unit>
    suspend fun completeAppointment(uid: String, appointmentId: String, paymentMethod: String): Result<Unit>
}

class FirestoreAppointmentDataSourceImpl : FirestoreAppointmentDataSource {

    private val firestore: FirebaseFirestore? by lazy {
        try {
            Firebase.firestore
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment ERROR] Failed to get Firebase.firestore: ${e.message}")
            null
        }
    }

    override suspend fun getAppointments(uid: String): List<AppointmentDto> {
        val fs = firestore ?: return emptyList()
        return try {
            val snapshot = fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .get()
            val list = snapshot.documents.mapNotNull { parseAppointmentDto(it) }
            println(">>> [FirestoreAppointment GET] Fetched ${list.size} appointments directly for $uid")
            list
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment GET ERROR] $uid: ${e.message}")
            emptyList()
        }
    }

    override fun observeAppointments(uid: String): Flow<List<AppointmentDto>> = flow {
        val fs = firestore
        if (fs == null) {
            println(">>> [FirestoreAppointment ERROR] firestore is NULL in observeAppointments")
            emit(emptyList())
            return@flow
        }

        // 1. Emit direct get result first for instant availability
        val direct = getAppointments(uid)
        emit(direct)

        // 2. Stream real-time updates safely
        try {
            fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .snapshots
                .map { snapshot ->
                    snapshot.documents.mapNotNull { parseAppointmentDto(it) }
                }
                .catch { e ->
                    println(">>> [FirestoreAppointment SNAPSHOT ERROR] $uid: ${e.message}")
                }
                .collect { list ->
                    emit(list)
                }
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment SNAPSHOT SETUP ERROR] ${e.message}")
        }
    }

    override suspend fun getAppointmentsForDate(uid: String, dateIso: String): List<AppointmentDto> {
        val fs = firestore ?: return emptyList()
        return try {
            val snapshot = fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .where { "date" equalTo dateIso }
                .get()
            val list = snapshot.documents.mapNotNull { parseAppointmentDto(it) }
            list
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment GET DATE ERROR] $uid / $dateIso: ${e.message}")
            emptyList()
        }
    }

    override fun observeAppointmentsForDate(uid: String, dateIso: String): Flow<List<AppointmentDto>> = flow {
        val fs = firestore
        if (fs == null) {
            emit(emptyList())
            return@flow
        }
        val direct = getAppointmentsForDate(uid, dateIso)
        emit(direct)

        try {
            fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .where { "date" equalTo dateIso }
                .snapshots
                .map { snapshot ->
                    snapshot.documents.mapNotNull { parseAppointmentDto(it) }
                }
                .catch { e ->
                    println(">>> [FirestoreAppointment DATE SNAPSHOT ERROR] $uid / $dateIso: ${e.message}")
                }
                .collect { list ->
                    emit(list)
                }
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment DATE SNAPSHOT SETUP ERROR] ${e.message}")
        }
    }

    override suspend fun getAppointmentsInRange(uid: String, startDateIso: String, endDateIso: String): List<AppointmentDto> {
        val fs = firestore ?: return emptyList()
        return try {
            val snapshot = fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .where {
                    "date" greaterThanOrEqualTo startDateIso
                    "date" lessThanOrEqualTo endDateIso
                }
                .get()
            val list = snapshot.documents.mapNotNull { parseAppointmentDto(it) }
            list
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment GET RANGE ERROR] $uid / $startDateIso..$endDateIso: ${e.message}")
            emptyList()
        }
    }

    override fun observeAppointmentsInRange(uid: String, startDateIso: String, endDateIso: String): Flow<List<AppointmentDto>> = flow {
        val fs = firestore
        if (fs == null) {
            emit(emptyList())
            return@flow
        }
        val direct = getAppointmentsInRange(uid, startDateIso, endDateIso)
        emit(direct)

        try {
            fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .where {
                    "date" greaterThanOrEqualTo startDateIso
                    "date" lessThanOrEqualTo endDateIso
                }
                .snapshots
                .map { snapshot ->
                    snapshot.documents.mapNotNull { parseAppointmentDto(it) }
                }
                .catch { e ->
                    println(">>> [FirestoreAppointment RANGE SNAPSHOT ERROR] $uid / $startDateIso..$endDateIso: ${e.message}")
                }
                .collect { list ->
                    emit(list)
                }
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment RANGE SNAPSHOT SETUP ERROR] ${e.message}")
        }
    }

    override suspend fun getAppointmentsPaged(uid: String, limit: Int, startAfterDate: String?): List<AppointmentDto> {
        val fs = firestore ?: return emptyList()
        return try {
            var q = fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .orderBy("date", dev.gitlive.firebase.firestore.Direction.DESCENDING)

            if (!startAfterDate.isNullOrBlank()) {
                q = q.startAfterFieldValues {
                    add(startAfterDate)
                }
            }

            val snapshot = q.limit(limit).get()
            snapshot.documents.mapNotNull { parseAppointmentDto(it) }
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment PAGED ERROR] $uid: ${e.message}")
            emptyList()
        }
    }

    override fun observeAppointmentById(uid: String, appointmentId: String): Flow<AppointmentDto?> = flow {
        val fs = firestore
        if (fs == null) {
            emit(null)
            return@flow
        }
        try {
            val docRef = fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .document(appointmentId)

            try {
                val initial = docRef.get()
                if (initial.exists) {
                    emit(parseAppointmentDto(initial))
                }
            } catch (e: Throwable) {
                println(">>> [FirestoreAppointment INITIAL GET ID ERROR] $appointmentId: ${e.message}")
            }

            docRef.snapshots
                .map { snap ->
                    if (snap.exists) parseAppointmentDto(snap) else null
                }
                .catch { e ->
                    println(">>> [FirestoreAppointment ID SNAPSHOT ERROR] $appointmentId: ${e.message}")
                }
                .collect { appt ->
                    emit(appt)
                }
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment ID SNAPSHOT SETUP ERROR] ${e.message}")
        }
    }

    private fun parseAppointmentDto(doc: DocumentSnapshot): AppointmentDto? {
        return try {
            val id = doc.id
            val clientName: String = try { doc.get<String?>("clientName") ?: "" } catch (_: Throwable) { "" }
            val clientPhone: String? = try { doc.get<String?>("clientPhone") } catch (_: Throwable) { null }
            val serviceName: String = try { doc.get<String?>("serviceName") ?: "" } catch (_: Throwable) { "" }

            val date: String = try {
                doc.get<String?>("date")?.takeIf { it.isNotBlank() }
            } catch (_: Throwable) { null }
                ?: try {
                    doc.get<String?>("data")?.takeIf { it.isNotBlank() }
                } catch (_: Throwable) { null }
                ?: ""

            val startTime: String = try {
                doc.get<String?>("startTime")?.takeIf { it.isNotBlank() }
            } catch (_: Throwable) { null }
                ?: try {
                    doc.get<String?>("time")?.takeIf { it.isNotBlank() }
                } catch (_: Throwable) { null }
                ?: try {
                    doc.get<String?>("hora")?.takeIf { it.isNotBlank() }
                } catch (_: Throwable) { null }
                ?: ""

            val endTime: String = try {
                doc.get<String?>("endTime")?.takeIf { it.isNotBlank() }
            } catch (_: Throwable) { null }
                ?: try {
                    doc.get<String?>("fim")?.takeIf { it.isNotBlank() }
                } catch (_: Throwable) { null }
                ?: ""

            val priceFormatted: String = try { doc.get<String?>("priceFormatted") ?: "" } catch (_: Throwable) { "" }
            val status: String = try {
                doc.get<String?>("status")?.takeIf { it.isNotBlank() } ?: "CONFIRMED"
            } catch (_: Throwable) { "CONFIRMED" }
            val paymentMethod: String = try { doc.get<String?>("paymentMethod") ?: "" } catch (_: Throwable) { "" }
            val notes: String? = try { doc.get<String?>("notes") } catch (_: Throwable) { null }
            val rawStartUtc: String? = try { doc.get<String?>("startUtcIso")?.takeIf { it.isNotBlank() } } catch (_: Throwable) { null }
            val rawEndUtc: String? = try { doc.get<String?>("endUtcIso")?.takeIf { it.isNotBlank() } } catch (_: Throwable) { null }

            // Backward compatibility: if startUtcIso / endUtcIso are missing, compute from date + startTime/endTime (studio UTC-3 = -180)
            val startUtcIso = rawStartUtc ?: if (date.isNotBlank() && startTime.isNotBlank()) {
                br.com.heyagenda.domain.util.DateTimeZoneUtils.localToUtcIso(date, startTime, -180)
            } else ""

            val endUtcIso = rawEndUtc ?: if (date.isNotBlank() && endTime.isNotBlank()) {
                br.com.heyagenda.domain.util.DateTimeZoneUtils.localToUtcIso(date, endTime, -180)
            } else ""

            AppointmentDto(
                id = id,
                clientName = clientName,
                clientPhone = clientPhone,
                serviceName = serviceName,
                date = date,
                startTime = startTime,
                endTime = endTime,
                priceFormatted = priceFormatted,
                status = status,
                paymentMethod = paymentMethod,
                notes = notes,
                startUtcIso = startUtcIso,
                endUtcIso = endUtcIso
            )
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment parse fallback] ${e.message}")
            try {
                doc.data<AppointmentDto>().copy(id = doc.id)
            } catch (_: Throwable) {
                null
            }
        }
    }

    override suspend fun saveAppointment(uid: String, appointment: AppointmentDto): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        return try {
            val docRef = if (appointment.id.isBlank()) {
                fs.collection("professionals").document(uid).collection("appointments").document
            } else {
                fs.collection("professionals").document(uid).collection("appointments").document(appointment.id)
            }
            val finalDto = appointment.copy(id = docRef.id)

            docRef.set(
                mapOf(
                    "id" to finalDto.id,
                    "clientName" to finalDto.clientName,
                    "clientPhone" to (finalDto.clientPhone ?: ""),
                    "serviceName" to finalDto.serviceName,
                    "date" to finalDto.date,
                    "startTime" to finalDto.startTime,
                    "endTime" to finalDto.endTime,
                    "priceFormatted" to finalDto.priceFormatted,
                    "status" to finalDto.status,
                    "paymentMethod" to finalDto.paymentMethod,
                    "notes" to (finalDto.notes ?: ""),
                    "startUtcIso" to finalDto.startUtcIso,
                    "endUtcIso" to finalDto.endUtcIso
                )
            )
            println(">>> [FirestoreAppointment SUCCESS] Saved appointment ${finalDto.id} for $uid")
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment SAVE ERROR] ${e.message}")
            Result.failure(e)
        }
    }

    override suspend fun updateAppointmentStatus(uid: String, appointmentId: String, status: String): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        return try {
            fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .document(appointmentId)
                .set(mapOf("status" to status), merge = true)
            println(">>> [FirestoreAppointment UPDATE SUCCESS] Updated $appointmentId status to $status")
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment UPDATE ERROR] ${e.message}")
            Result.failure(e)
        }
    }

    override suspend fun updateAppointmentPaymentMethod(uid: String, appointmentId: String, paymentMethod: String): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        return try {
            fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .document(appointmentId)
                .set(mapOf("paymentMethod" to paymentMethod), merge = true)
            println(">>> [FirestoreAppointment PAYMENT SUCCESS] Updated $appointmentId paymentMethod to $paymentMethod")
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment PAYMENT ERROR] ${e.message}")
            Result.failure(e)
        }
    }

    override suspend fun completeAppointment(uid: String, appointmentId: String, paymentMethod: String): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        return try {
            val payload = mapOf(
                "status" to "COMPLETED",
                "paymentMethod" to paymentMethod
            )
            fs.collection("professionals")
                .document(uid)
                .collection("appointments")
                .document(appointmentId)
                .set(payload, merge = true)
            println(">>> [FirestoreAppointment COMPLETE SUCCESS] Completed $appointmentId with payment $paymentMethod")
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreAppointment COMPLETE ERROR] ${e.message}")
            Result.failure(e)
        }
    }
}
